import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';

/**
 * Circuit-indicatorkleur per keelpoort, gelijk aan de circuit-source-of-truth
 * (`HD_BODYGRAPH_CIRCUITS` in `hd-bodygraph-circuit-metadata.ts`): collectief-
 * logisch #05A1F9, collectief-abstract #416CAC, individueel-weten #00C165,
 * tribaal-ondernemend #F01C1C. De cheat-sheet-pagina gebruikt (nog) haar eigen
 * pastel-kleuren uit het Figma-design; het paneel kleurt bewust op circuit.
 * Poort 20 zit in twee individuele circuits en krijgt een split-rail
 * (weten + integratie), net als poort 57 bij de angsten.
 */
export const HD_VOICE_GATE_COLOR: Readonly<Record<number, string>> = {
  62: '#05A1F9', 16: '#05A1F9', 31: '#05A1F9',
  56: '#416CAC', 35: '#416CAC', 33: '#416CAC',
  23: '#00C165', 12: '#00C165', 8: '#00C165', 20: '#00C165',
  45: '#F01C1C',
};

/** Tweede indicatorkleur (split-rail) voor poorten in twee circuits. */
export const HD_VOICE_GATE_COLOR_2: Readonly<Record<number, string>> = {
  20: '#00522B', // individueel-integratie naast individueel-weten
};

interface VoiceCardVm {
  readonly rail: string;
  /** Stem-keynote uit de gates-scope ("Ik ben nu", "I know", …). */
  readonly voiceKeynote: string;
  /** Voice-eigenschappen uit de gates-scope ("besluitvol / besluiteloos", …). */
  readonly voiceProperties: string;
  /** Poort-keynotes-lijn uit de communication-scope ("Contemplatie, richting van het nu"). */
  readonly keynotes: string;
}

/**
 * Het voice-"blokje" van een keelpoort, opgebouwd zoals het angst-blokje
 * ({@link HdFearCardComponent}): poortnummer, de stem-keynote met haar
 * eigenschappen, en rechts de poort-keynotes, met de circuit-indicatorkleur
 * als linker rail. Leest zijn content zelf uit de `human-design/gates`-
 * (voice) en `human-design/communication`-scopes (taalwissel + NL-fallback
 * inbegrepen), zodat het blokje overal identiek oogt.
 */
@Component({
  selector: 'hd-voice-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hd-voice-card',
    '[class.hd-voice-card--compact]': "size() === 'compact'",
    '[attr.data-gate]': 'gate()',
  },
  template: `
    <span class="hd-voice-card__rail" [style.background]="vm().rail"></span>
    <span class="hd-voice-card__gate">{{ gate() }}</span>
    <div class="hd-voice-card__body">
      <p class="hd-voice-card__keynote">{{ vm().voiceKeynote }}</p>
      <p class="hd-voice-card__properties">{{ vm().voiceProperties }}</p>
    </div>
    <div class="hd-voice-card__meta">
      <p class="hd-voice-card__keynotes">{{ vm().keynotes }}</p>
    </div>
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      background: var(--white);
      border: 1px solid #e6e6e6;
      border-radius: 6px;
      padding: 8px 14px 8px 18px;
      color: #3d414e;
      text-align: left;
    }

    .hd-voice-card__rail {
      position: absolute;
      left: 0;
      top: 5px;
      bottom: 5px;
      width: 5px;
      border-radius: 3px;
    }

    .hd-voice-card__gate {
      flex: 0 0 auto;
      min-width: 40px;
      text-align: center;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 26px;
      line-height: 1;
    }

    .hd-voice-card__body {
      flex: 1 1 auto;
      min-width: 0;
    }

    .hd-voice-card__keynote {
      margin: 0;
      font-weight: 800;
      font-size: 15.5px;
      line-height: 1.2;
    }

    .hd-voice-card__properties {
      margin: 1px 0 0;
      font-size: 12px;
      color: rgba(61, 65, 78, 0.7);
    }

    .hd-voice-card__meta {
      flex: 0 0 auto;
      max-width: 42%;
      text-align: right;
    }

    .hd-voice-card__keynotes {
      margin: 0;
      font-size: 11.5px;
      color: rgba(61, 65, 78, 0.55);
    }

    /* Compacte variant voor smalle panelen: zelfde opbouw, kleinere maten. */
    :host(.hd-voice-card--compact) {
      gap: 8px;
      padding: 6px 10px 6px 14px;
    }

    :host(.hd-voice-card--compact) .hd-voice-card__gate {
      min-width: 30px;
      font-size: 20px;
    }

    :host(.hd-voice-card--compact) .hd-voice-card__keynote {
      font-size: 13.5px;
    }

    :host(.hd-voice-card--compact) .hd-voice-card__properties {
      font-size: 10.5px;
    }

    :host(.hd-voice-card--compact) .hd-voice-card__keynotes {
      font-size: 10px;
    }
  `,
})
export class HdVoiceCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly gate = input.required<number>();
  /** `compact` maakt het blokje smaller-vriendelijk voor gedockte panelen. */
  readonly size = input<'default' | 'compact'>('default');

  protected readonly vm = computed<VoiceCardVm>(() => {
    const gate = this.gate();
    const color = HD_VOICE_GATE_COLOR[gate] ?? '#3d414e';
    const color2 = HD_VOICE_GATE_COLOR_2[gate];
    const voice = this.gateInfo.gateInfoIn(gate, this.contentLang.effectiveLang()).translations.voice;
    return {
      rail: color2 ? `linear-gradient(180deg, ${color2} 50%, ${color} 50%)` : color,
      voiceKeynote: voice?.keynote ?? '',
      voiceProperties: voice?.properties ?? '',
      keynotes: this.text(`gates.${gate}.keynotes`),
    };
  });

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('communication', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('communication', key, 'nl');
  }
}
